import clsx from 'clsx'

import styles from './style.module.scss'

/**
 * Schlanke, eigenstaendige Formularfelder.
 *
 * Die bestehenden UI-Felder (TextField, SelectField, Checkbox) sind fest an
 * react-hook-form gebunden: TextField ruft in jedem onChange ungeprueft
 * props.register.onChange auf, SelectField rendert einen Controller und
 * Checkbox erwartet ein register-Objekt. Ohne Formularkontext brechen sie zur
 * Laufzeit.
 *
 * Das Unterstuetzer:innen-Modul arbeitet mit einfachem lokalem State, deshalb
 * diese kontrollierten Felder im gleichen visuellen Stil.
 */

/**
 * `inputRef` gibt das eigentliche Eingabefeld nach außen. Gebraucht wird das,
 * wo der Fokus gesetzt werden soll, ohne zu scrollen — `focus({ preventScroll:
 * true })` gibt es nur als Aufruf, nicht als `autoFocus`-Attribut.
 */
export function PlainInput({ label, error, className, required, inputRef, ...rest }) {
	return (
		<label className={clsx(styles.root, className)}>
			{label && (
				<span className={styles.label}>
					{label} {required && <span className={styles.required}>*</span>}
				</span>
			)}
			<input
				ref={inputRef}
				className={clsx(styles.input, error && styles.inputError)}
				{...rest}
			/>
			{error && <span className={styles.error}>{error}</span>}
		</label>
	)
}

export function PlainTextarea({ label, error, className, rows = 4, ...rest }) {
	return (
		<label className={clsx(styles.root, className)}>
			{label && <span className={styles.label}>{label}</span>}
			<textarea
				rows={rows}
				className={clsx(styles.input, styles.textarea, error && styles.inputError)}
				{...rest}
			/>
			{error && <span className={styles.error}>{error}</span>}
		</label>
	)
}

/**
 * options: [{ value, label }]
 * Bei placeholder wird eine leere Auswahl vorangestellt.
 */
export function PlainSelect({ label, error, className, options = [], placeholder, ...rest }) {
	return (
		<label className={clsx(styles.root, className)}>
			{label && <span className={styles.label}>{label}</span>}
			<select className={clsx(styles.input, styles.select, error && styles.inputError)} {...rest}>
				{placeholder && <option value="">{placeholder}</option>}
				{options.map(option => (
					// `disabled` an der einzelnen Auswahl: gesperrte Werte bleiben
					// sichtbar, statt zu fehlen. Wer sie nicht setzen darf, soll
					// trotzdem sehen, dass es sie gibt.
					<option key={option.value} value={option.value} disabled={option.disabled}>
						{option.label}
					</option>
				))}
			</select>
			{error && <span className={styles.error}>{error}</span>}
		</label>
	)
}

export function PlainCheckbox({ label, checked, onChange, disabled, className }) {
	return (
		<label className={clsx(styles.checkboxRoot, className)}>
			<input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
			<span>{label}</span>
		</label>
	)
}
